import { useCallback, useEffect, useState, type FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { Table } from '../../../Table';
import { Modal } from '../../../Modal';
import { ManagerTeamTrainingCard } from '../ManagerTeamTrainingCard';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { useTeamTrainingCompletionData } from './hooks/useTeamTrainingCompletionData';
import {
  useManagerTeamCompleationQuery,
  type TeamCompleationTrainingQueryParams,
} from '../../../../apiHooks/useManagerTeamCompleationQuery';
import { Pagination } from '../../../Pagination';
import { useTeamTrainingCompleationFilters } from './hooks/useTeamTrainingCompleationFilters';
import { useCampaignSendNudgeMutation } from '../../../../apiHooks/useCampaignSendNudgeMutation';
import { NudgeModal, type NudgeModalConfigs } from '../../../Nudge/NudgeModal';
import toast from 'react-hot-toast';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { useNamedContentEntryText } from '../../../../lib/hooks/contentEntry/useNamedContentEntryText';
import { TrainingPrimaryButton } from '../TrainingCard';
import { NudgeType } from '../../../Nudge/enums/NudgeType';
import ExportActivityLogButton from '../../../Button/DownloadButton/ExportActivityLogButton/ExportActivityLogButton';

const DEFAULT_PER_PAGE: number = 10 as const;

type TrainingCompletionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  statsCard?: {
    totalLearners: number;
    learnersCompletedTotal: number;
    estimatedCompletionTime: number;
    title: string;
    dueDate: string;
  };
  campaignId: string;
};

export enum TeamDetailsTabs {
  NonCompleters = 'nonCompleters',
  Completed = 'completed',
}

export const TrainingCompletionModal: FC<TrainingCompletionModalProps> = ({
  isOpen,
  onClose,
  statsCard,
  campaignId,
}: TrainingCompletionModalProps) => {
  const [selectedTab, setSelectedTab] = useState<TeamDetailsTabs>(TeamDetailsTabs.NonCompleters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [queryParams, setQueryParams] = useState<TeamCompleationTrainingQueryParams>({
    page: { skip: 0, take: DEFAULT_PER_PAGE },
    campaignId,
    completers: false,
  });
  const { data: teamMembers, isLoading, isFetching } = useManagerTeamCompleationQuery(queryParams);
  const [nudgeModalConfigs, setNudgeModalConfigs] = useState<NudgeModalConfigs>({
    isOpen: false,
  });

  const onNewParams = useCallback((params: Partial<TeamCompleationTrainingQueryParams>) => {
    setQueryParams((prev) => ({ ...prev, ...params }));
  }, []);

  useEffect(() => {
    onNewParams({ completers: selectedTab === TeamDetailsTabs.Completed });
  }, [selectedTab, onNewParams]);

  const { nameSearchInputValue, handleNameSearchChange } = useTeamTrainingCompleationFilters(onNewParams);

  const handlePageChange = (page: number) => {
    onNewParams({ page: { take: DEFAULT_PER_PAGE, skip: (page - 1) * DEFAULT_PER_PAGE } });
    setCurrentPage(page);
  };

  const handleClose = () => {
    setQueryParams({
      page: { skip: 0, take: DEFAULT_PER_PAGE },
      campaignId,
      completers: false,
    });
    onClose();
  };

  const successToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeSentSuccessfully',
  });
  const failToastText = useNamedContentEntryText({
    refKey: 'managerTrainingNudgeDialog',
    subKey: 'nudgeFailure',
  });

  const { mutate, isPending } = useCampaignSendNudgeMutation();

  const theme = useTheme();

  const handleSendNudge = () => {
    mutate(
      {
        campaignId,
        userId: nudgeModalConfigs.userId,
      },
      {
        onSuccess: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon icon={IconType.CHECK_CIRCLE} width={20} height={20} fill="transparent" />
                <span>{successToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.prototype.foreground,
                color: theme.colors.prototype.primaryForeground,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
        onError: () => {
          toast(
            (t) => (
              <NudgeToast>
                <Icon
                  className="alert-triangle"
                  icon={IconType.ALERT_TRIANGLE}
                  width={20}
                  height={20}
                  fill="transparent"
                />
                <span>{failToastText}</span>
                <Icon onClick={() => toast.dismiss(t.id)} icon={IconType.TIMES} width={24} height={24} />
              </NudgeToast>
            ),
            {
              position: 'bottom-center',
              style: {
                background: theme.colors.prototype.foreground,
                color: theme.colors.prototype.primaryForeground,
                padding: '8px 0',
              },
            },
          );
          setNudgeModalConfigs({
            isOpen: false,
          });
        },
      },
    );
  };

  const { columns } = useTeamTrainingCompletionData(
    nameSearchInputValue,
    handleNameSearchChange,
    setNudgeModalConfigs,
    onNewParams,
    queryParams,
  );

  return (
    <Modal title="Completion Status" isOpen={isOpen} onClose={handleClose}>
      <ModalContainer>
        <Content>
          <ManagerTeamTrainingCard
            hideTooltip
            campaignId={campaignId}
            title={statsCard?.title || ''}
            dueDate={statsCard?.dueDate || ''}
            totalLearners={statsCard?.totalLearners || 0}
            learnersCompletedTotal={statsCard?.learnersCompletedTotal || 0}
            estimatedCompletionTime={statsCard?.estimatedCompletionTime || 0}
            nudgeActionText={null}
          />
          <TabUtilityContainer>
            <TabFilterContainer>
              <TabSection onClick={() => setSelectedTab(TeamDetailsTabs.NonCompleters)}>
                <TabSectionTitle $isSelected={selectedTab === TeamDetailsTabs.NonCompleters}>
                  <NamedContentEntryText
                    container="span"
                    refKey="managerTrainingCompletionDialog"
                    subKey="nonCompleters"
                  />
                </TabSectionTitle>
                <Delimiter isSelected={selectedTab === TeamDetailsTabs.NonCompleters} />
              </TabSection>
              <TabSection onClick={() => setSelectedTab(TeamDetailsTabs.Completed)}>
                <TabSectionTitle $isSelected={selectedTab === TeamDetailsTabs.Completed}>
                  <NamedContentEntryText
                    container="span"
                    refKey="managerTeamCyberSecurityTraining"
                    subKey="completed"
                  />
                </TabSectionTitle>
                <Delimiter isSelected={selectedTab === TeamDetailsTabs.Completed} />
              </TabSection>
            </TabFilterContainer>
            <RightTabActions>
              <DownloadSection>
                <ExportActivityLogButton />
              </DownloadSection>
              <div style={{ width: 16 }} />
              {statsCard?.learnersCompletedTotal !== statsCard?.totalLearners && (
                <TrainingPrimaryButton
                  onClick={() => {
                    setNudgeModalConfigs({
                      isOpen: true,
                      type: NudgeType.REMINDER,
                    });
                  }}
                >
                  <NamedContentEntryText
                    container="span"
                    refKey="managerTrainingCompletionDialog"
                    subKey="nudgeAllNonCompleters"
                  />
                </TrainingPrimaryButton>
              )}
            </RightTabActions>
            {/* TODO: Uncomment after csv issue is fixed  */}
          </TabUtilityContainer>
          <TableWrapper>
            <Table data={teamMembers.values} columns={columns} isLoading={isLoading || isFetching} />
            <Pagination
              pages={Math.ceil(teamMembers.total / DEFAULT_PER_PAGE)}
              currentPage={currentPage}
              perPage={DEFAULT_PER_PAGE}
              listSize={teamMembers.total}
              onChange={handlePageChange}
            />
          </TableWrapper>
        </Content>
        <NudgeModal
          nudgeConfigs={nudgeModalConfigs}
          onClose={() => setNudgeModalConfigs({ isOpen: false })}
          onSendNudge={handleSendNudge}
          isSending={isPending}
        />
      </ModalContainer>
    </Modal>
  );
};

const RightTabActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 24px;
  gap: 24px;
  min-height: 700px;
  width: 924px;
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const TabUtilityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const TabFilterContainer = styled.div`
  width: 30%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const TabSection = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

const DownloadSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 70%;
  align-items: end;
`;

const TabSectionTitle = styled.div<{ $isSelected: boolean }>`
  font-weight: ${({ $isSelected }) => ($isSelected ? 'bold' : 'normal')};
`;

const Delimiter = styled.div<{ isSelected?: boolean }>`
  height: 2px;
  background-color: ${({ isSelected }) => (isSelected ? '#5170EF' : '#B6BEDC')};
  margin-top: 5px;
  width: 100%;
`;

const TableWrapper = styled.div`
  width: 100%;
  min-height: 450px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const NudgeToast = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > svg {
    cursor: pointer;

    path {
      stroke: #fff;
    }
  }
`;
